import { useState, useEffect } from "react";
import { throttle } from "@utils/throttle";
import { BREAKPOINTS } from "@utils/breakpoints";

/**
 * Hook to get window dimensions and optionally check if width is below a breakpoint
 * @param {string|number} [breakpoint] - Optional breakpoint key (e.g. 'md', 'lg', 1200, 1024) or pixel value to check if width is below
 * @returns {Object} Window dimensions and isBelow flag if breakpoint provided
 */

export function useWindowSize(breakpoint) {
  const [windowSize, setWindowSize] = useState(() => {
    if (typeof window === "undefined") {
      return { width: 0, height: 0 };
    }
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    const throttledResize = throttle(handleResize, 100);
    window.addEventListener("resize", throttledResize);

    return () => {
      window.removeEventListener("resize", throttledResize);
    };
  }, []);

  if (breakpoint !== undefined) {
    const breakpointValue = typeof breakpoint === "string" ? BREAKPOINTS[breakpoint] : breakpoint;

    if (breakpointValue === undefined) {
      console.warn(`Invalid breakpoint: ${breakpoint}`);
      return windowSize;
    }

    return {
      ...windowSize,
      isBelowBreakpoint: windowSize.width < breakpointValue,
    };
  }

  return windowSize;
}
